import ModalPortal from '@/components/ModalPortal';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Building2, Monitor, Cpu, Key, Trash2, Search,
    Loader2, X, Check, MessageSquare, Building, Pencil
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CoreDialog from '@/components/CoreDialog';
import AdministrationPage from './AdministrationPage';
import api from '@/api/axios';
import {
    formatCnpj,
    formatPhone,
    isValidCnpjFormat,
    isValidPhoneFormat,
} from '@/utils/formValidation';
import {
    buildSettingsSearchParams,
    getSettingsItemPrimaryLabel,
} from '@/utils/systemSettings';


// --- TIPAGEM ---
export interface GenericEntity {
    id: string;
    name?: string;
    displayName?: string;
    keyName?: string;
    fantasyName?: string; // Adicionado para Empresas
    affectsRanking?: boolean;
    isUnique?: boolean;
}

interface SpringPageResponse<T> {
    content: T[];
    last: boolean;
    totalElements: number;
}

// --- CONFIGURAÇÃO DE MAPEAMENTO ---
const CONFIG_TABS = [
    { id: 'brands', label: 'Marcas', icon: Building, fields: ['Marca'] },
    { id: 'hardware', label: 'Equipamentos', icon: Cpu, fields: ['Tipo de Equipamento', 'Modelos de Equipamentos'] },
    { id: 'components', label: 'Peças e Componentes', icon: Cpu, fields: ['Tipo de Item', 'Modelos de Itens'] },
    { id: 'materials', label: 'Materiais e Suprimentos', icon: Building, fields: ['Tipo de Item', 'Modelos de Itens'] },
    { id: 'software', label: 'Sistemas Operacionais', icon: Monitor, fields: ['Família OS', 'Sist. Operacional'] },
    { id: 'calls', label: 'Chamados', icon: MessageSquare, fields: ['Tipos de Chamado', 'Categorias', 'SubCategorias'] }, // Nova Aba
    { id: 'tech', label: 'Parâmetros técnicos', icon: Key, fields: ['Chaves de Especificação'] },
];

const endpointMap: Record<string, string> = {
    'Equipe de TI': 'it-teams',
    'Secretaria': 'secretary',
    'Unidade': 'unit',
    'Local': 'local',
    'Marca': 'brand',
    'Tipo de Equipamento': 'equipment-type',
    'Modelos de Equipamentos': 'equipment-model',
    'Tipo de Item': 'item-type',
    'Modelos de Itens': 'item-model',
    'Família OS': 'os-family',
    'Sist. Operacional': 'os',
    'Chaves de Especificação': 'specification-Key',
    'Tipos de Chamado': 'call-type',
    'Categorias': 'category',
    'SubCategorias': 'sub-category',
    'Empresas Terceirizadas': 'outsourced-company',
};

const dependencyMap: Record<string, { label: string; endpoint: string; jsonKey: string }[]> = {
    'Secretaria': [{ label: 'Equipe de TI', endpoint: 'it-teams', jsonKey: 'itTeam' }],
    'Unidade': [{ label: 'Secretaria', endpoint: 'secretary', jsonKey: 'secretary' }],
    'Local': [
        { label: 'Secretaria', endpoint: 'secretary', jsonKey: 'secretary' },
        { label: 'Unidade', endpoint: 'unit', jsonKey: 'unit' }
    ],
    'Modelos de Equipamentos': [
        { label: 'Marca', endpoint: 'brand', jsonKey: 'brand' },
        { label: 'Tipo de Equipamento', endpoint: 'equipment-type', jsonKey: 'equipmentType' }
    ],
    'Modelos de Itens': [
        { label: 'Marca', endpoint: 'brand', jsonKey: 'brand' },
        { label: 'Tipo de Item', endpoint: 'item-type', jsonKey: 'itemType' }
    ],
    'Sist. Operacional': [{ label: 'Família OS', endpoint: 'os-family', jsonKey: 'family' }],
    'SubCategorias': [{ label: 'Categorias', endpoint: 'category', jsonKey: 'category' }] // Dependência Adicionada
};

// --- COMPONENTE: LISTA COM INFINITE SCROLL ---
const InfiniteList = ({ field, category, refreshKey, onEdit }: { field: string; category?: string; refreshKey: number, onEdit: (item: GenericEntity) => void }) => {
    const [items, setItems] = useState<GenericEntity[]>([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);
    const requestVersionRef = useRef(0);
    const [itemToDelete, setItemToDelete] = useState<GenericEntity | null>(null);

    const loadData = useCallback(async (pageNum: number, clear: boolean = false, currentSearch: string = '') => {
        const endpoint = endpointMap[field];
        if (!endpoint) return;

        setLoading(true);
        const requestVersion = clear ? ++requestVersionRef.current : requestVersionRef.current;
        try {
            const res = await api.get<SpringPageResponse<GenericEntity>>(`/${endpoint}/all`, {
                params: {
                    page: pageNum,
                    size: 8,
                    sort: 'id,asc',
                    category,
                    ...buildSettingsSearchParams(field, currentSearch),
                }
            });
            if (requestVersion !== requestVersionRef.current) return;
            const content = res.data.content || [];
            setItems(prev => clear ? content : [...prev, ...content]);
            setHasMore(res.data.last !== undefined ? !res.data.last : pageNum + 1 < ((res.data as any).page || res.data).totalPages);
        } catch (err) {
            if (requestVersion === requestVersionRef.current) console.error(err);
        } finally {
            if (requestVersion === requestVersionRef.current) setLoading(false);
        }
    }, [field, category]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setPage(0);
            loadData(0, true, searchTerm);
        }, 300); // Espera 300ms após parar de digitar

        return () => clearTimeout(delayDebounce);
    }, [searchTerm, refreshKey, loadData]);

    const handleScroll = () => {
        if (!scrollRef.current || loading || !hasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
            const next = page + 1;
            setPage(next);
            loadData(next, false, searchTerm);
        }
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await api.delete(`/${endpointMap[field]}/${itemToDelete.id}`);
            setItemToDelete(null); // Fecha o dialog
            loadData(0, true, searchTerm); // Recarrega a lista sem quebrar o scroll
        } catch (err) {
            console.error(err);
            alert("Não foi possível excluir o item. Verifique se ele possui vínculos.");
            setItemToDelete(null);
        }
    };

    return (
        <div className="space-y-3">
            <CoreDialog
                isOpen={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onConfirm={handleConfirmDelete}
                type="danger"
                title="Confirmar Exclusão"
                description={
                    <>
                        Você está prestes a remover <span className="text-red-400 font-black italic">"{itemToDelete?.name || itemToDelete?.displayName || itemToDelete?.keyName || itemToDelete?.fantasyName}"</span>.
                        <br/>Esta ação não pode ser desfeita.
                    </>
                }
                confirmLabel="Excluir Registro"
            />

            <div className="relative group">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-cyan-500 transition-colors" />
                <input
                    type="text"
                    placeholder={`Buscar em ${field}...`}
                    className="w-full bg-[#0b0f1a]/30 border border-white/5 rounded-lg pl-9 pr-4 py-2 text-[10px] text-white outline-none focus:border-cyan-500/30 focus:ring-1 focus:ring-cyan-500/20 transition-all uppercase placeholder:text-slate-700"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div ref={scrollRef} onScroll={handleScroll}
                 className="space-y-1 max-h-40 overflow-y-scroll pr-2 custom-scrollbar">
                {items.map(item => (
                    <div key={`${item.id}-${field}`}
                         className="group flex flex-col p-3 bg-[#0b0f1a]/50 border border-white/5 rounded-xl hover:border-cyan-500/20 transition-all w-full overflow-hidden">

                        {/* TABELA AJUSTADA PARA PERMITIR QUEBRA DE TEXTO */}
                        <table className="w-full border-collapse">
                            <tbody>
                            <tr>
                                {/* COLUNA DO TEXTO: Agora permite quebra de linha natural */}
                                <td className="align-middle pr-4 whitespace-normal break-words">
                        <span className="text-[12px] font-black text-slate-300 group-hover:text-white uppercase tracking-tight block whitespace-normal break-words leading-tight">
                            {getSettingsItemPrimaryLabel(field, item)}
                        </span>
                                </td>

                                {/* COLUNA DOS BOTÕES: Continua fixa e alinhada no topo/meio da direita */}
                                <td className="w-[45px] text-right align-middle whitespace-nowrap flex-shrink-0">
                                    <div className="inline-flex items-center gap-2">
                                        <button
                                            onClick={() => onEdit(item)}
                                            className="text-slate-500 hover:text-orange-500 transition-all p-0.5 md:opacity-0 group-hover:opacity-100"
                                            title="Editar"
                                        >
                                            <Pencil size={13}/>
                                        </button>
                                        {field !== 'Equipe de TI' && <button
                                            onClick={() => setItemToDelete(item)}
                                            className="text-slate-500 hover:text-red-500 transition-all p-0.5 md:opacity-0 group-hover:opacity-100"
                                            title="Excluir"
                                        >
                                            <Trash2 size={13}/>
                                        </button>}
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        {/* SUB-LABELS: Também ajustados para quebrar linha se forem gigantes */}
                        <div className="flex flex-col gap-0.5 mt-1.5 whitespace-normal break-words">
                            {field === 'Secretaria' && (item as any).itTeam?.name && (
                                <span className="text-xs text-slate-400">{(item as any).itTeam.name}</span>
                            )}
                            {field === 'Unidade' && (item as any).secretary?.name && (
                                <span className="text-[11px] text-slate-500 font-bold tracking-wide block leading-tight">
                    {(item as any).secretary.name}
                </span>
                            )}
                            {field === 'Local' && <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-400">
                                <span>Recebe Materiais?</span>
                                <button type="button" role="switch" disabled aria-label="Recebe Materiais?" aria-checked={!!(item as any).receives}
                                    className={`relative h-5 w-9 rounded-full ${(item as any).receives ? 'bg-emerald-600' : 'bg-slate-500'}`}>
                                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white ${(item as any).receives ? 'left-[18px]' : 'left-0.5'}`} />
                                </button>
                            </div>}
                            {field === 'Local' && (item as any).unit?.name && (
                                <div className="flex flex-col mt-0.5 gap-0.5">
                    <span className="text-[11px] text-slate-500 font-bold tracking-wide block leading-tight">
                        {(item as any).unit.name}
                    </span>
                                    <span className="text-[11px] text-slate-600 font-bold tracking-wide block leading-tight">
                        {(item as any).unit.secretary?.name}
                    </span>
                                </div>
                            )}

                            {(field === 'Modelos de Equipamentos' || field === 'Modelos de Itens') && (item as any).brand && (
                                <div className="flex flex-col mt-0.5">
                                    <span className="text-[11px] text-slate-500 font-bold tracking-wide leading-tight">
                                        {(item as any).type || (item as any).equipmentType?.name}
                                    </span>
                                    <span className="text-[11px] text-slate-500 font-bold tracking-wide leading-tight">
                                        {typeof (item as any).brand === 'string' ? (item as any).brand : (item as any).brand?.name}
                                    </span>
                                </div>
                            )}

                            {field === 'Sist. Operacional' && (item as any).family?.name && (
                                <span className="text-[11px] text-slate-500 font-bold tracking-wide leading-tight">
                                    {(item as any).family.name}
                                </span>
                            )}

                            {field === 'Chaves de Especificação'
                                && item.displayName
                                && item.displayName !== item.keyName && (
                                <span className="text-[11px] text-slate-500 font-bold tracking-wide leading-tight">
                                    {item.displayName}
                                </span>
                            )}

                            {field === 'SubCategorias' && (item as any).category?.name && (
                                <span className="text-[11px] text-cyan-500/50 font-bold tracking-wide leading-tight">
                                    {(item as any).category.name}
                                </span>
                            )}

                            {field === 'Empresas Terceirizadas' && (
                                <div className="flex flex-col mt-0.5">
                                    <span className="text-[12px] text-cyan-500/50 font-bold tracking-wide leading-tight">
                                        {(item as any).fantasyName}
                                    </span>
                                    <span className="text-[10px] text-slate-500 font-medium tracking-wide">
                                        {(item as any).contact || 'NÃO INFORMADO'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {loading &&
                    <div className="py-2 flex justify-center"><Loader2 size={14} className="animate-spin text-cyan-500"/>
                    </div>}
            </div>
        </div>
    );
};

// --- COMPONENTE: MODAL DE CADASTRO ---
const RegistrationModal = ({ field, category, onClose, onSave, initialData }: { category?: string; field: string; onClose: () => void; onSave: (data: any) => void; initialData?: any }) => {
    const [formData, setFormData] = useState<any>(() => ({
        affectsRanking: false, isUnique: false, category,
        ...(initialData || {}),
        name: initialData?.name || initialData?.keyName || '',
        cnpj: formatCnpj(initialData?.cnpj || ''),
        telephone: formatPhone(initialData?.telephone || ''),
        receives: initialData?.receives ?? false,
    }));
    const isEditing = !!initialData;
    const [depsData, setDepsData] = useState<Record<string, any[]>>({});
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [formError, setFormError] = useState('');

    useEffect(() => {
        const fetchInitialDeps = async () => {
            const deps = dependencyMap[field] || [];
            const results: Record<string, any[]> = {};
            for (const dep of deps) {
                if (field === 'Local' && dep.label === 'Unidade') continue;
                try {
                    const options: any[] = []; let page = 0; let pages = 1;
                    do {
                        const res = await api.get(`/${dep.endpoint}/all`, { params: { page, size: 100, category: dep.endpoint === 'item-type' ? category : undefined } });
                        options.push(...(res.data.content || res.data)); pages = (res.data.page || res.data).totalPages || 1; page++;
                    } while (page < pages);
                    results[dep.label] = options;
                } catch (e) { console.error(e); }
            }
            setDepsData(results);
        };
        fetchInitialDeps();
    }, [field, category]);

    useEffect(() => {
        // Se estiver editando um Local e ele tiver uma secretaria vinculada
        if (isEditing && field === 'Local' && initialData?.unit?.secretary?.id) {
            handleSecretariaChange(initialData.unit.secretary.id);
        }
    }, [isEditing, field, initialData]);

    const handleSecretariaChange = async (secretariaId: string) => {
        if (!secretariaId) {
            setDepsData(prev => ({ ...prev, 'Unidade': [] }));
            return;
        }
        setLoadingUnits(true);
        try {
            const res = await api.get(`/unit/secretary/${secretariaId}`);
            setDepsData(prev => ({ ...prev, 'Unidade': res.data.content || res.data }));
        } catch (e) { console.error(e); } finally { setLoadingUnits(false); }
    };

    const handleSubmit = () => {
        setFormError('');

        if (field === 'Empresas Terceirizadas') {
            if (!formData.fantasyName?.trim()) {
                setFormError('Informe o nome fantasia da empresa.');
                return;
            }
            if (!isValidCnpjFormat(formData.cnpj || '')) {
                setFormError('Informe um CNPJ válido, como 14.397.462/0001-09 ou A1.B2C.3D4/1A2B-99.');
                return;
            }
            if (formData.telephone && !isValidPhoneFormat(formData.telephone)) {
                setFormError('Informe um telefone no formato (99)9999-9999 ou (99)99999-9999.');
                return;
            }
        }

        onSave(formData);
    };

    return (
        <ModalPortal><motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-[#161e2d] border border-white/10 w-full max-w-md rounded-[2rem] p-8 shadow-2xl relative">
                <button onClick={onClose} className="absolute top-6 right-6 text-slate-500 hover:text-white"><X size={20}/></button>
                <h2 className="text-white font-black uppercase italic text-sm tracking-widest mb-6 italic text-glow">
                    {isEditing ? 'EDITAR_' : 'NOVO_'}<span className="text-cyan-500">{field}</span>
                </h2>

                <div className="space-y-5">
                    {field !== 'Local' && dependencyMap[field]?.map(dep => (
                        <div key={dep.label}>
                            <label className="text-[10px] font-black uppercase text-slate-600 ml-1">{dep.label}</label>
                            <select
                                className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                value={formData[`${dep.jsonKey}Id`] || formData[dep.jsonKey]?.id || ''}
                                onChange={e => setFormData({...formData, [`${dep.jsonKey}Id`]: e.target.value})}
                            >
                                <option value="">SELECIONE...</option>
                                {depsData[dep.label]?.map(item => <option key={item.id} value={item.id}>{item.name || item.displayName}</option>)}
                            </select>
                        </div>
                    ))}

                    {field === 'Local' && (
                        <>
                            <div>
                                <label className="text-[10px] font-black uppercase text-slate-600 ml-1">1. Secretaria</label>
                                <select
                                    className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                    value={formData.unit?.secretary?.id || formData.secretaryId || ''}
                                    onChange={e => {
                                        setFormData({...formData, secretaryId: e.target.value, unitId: ''});
                                        handleSecretariaChange(e.target.value);
                                    }}
                                >
                                    <option value="">SELECIONE...</option>
                                    {depsData['Secretaria']?.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase text-slate-600 ml-1">2. Unidade</label>
                                <div className="relative">
                                    <select
                                        disabled={!depsData['Unidade']?.length || loadingUnits}
                                        className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-30"
                                        value={formData.unitId || formData.unit?.id || ''}
                                        onChange={e => setFormData({...formData, unitId: e.target.value})}
                                    >
                                            <option value="">{loadingUnits ? "CARREGANDO..." : "SELECIONE A UNIDADE..."}</option>
                                            {depsData['Unidade']?.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                    </select>
                                    {loadingUnits && <Loader2 size={14} className="absolute right-8 top-4 animate-spin text-cyan-500" />}
                                </div>
                            </div>
                        </>
                    )}

                    {field !== 'Empresas Terceirizadas' && <div>
                        <label className="text-[10px] font-black uppercase text-slate-600 ml-1">{field}</label>
                        <input
                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                            value={formData.name || ''}
                            onChange={e => setFormData({...formData, name: e.target.value})}
                        />
                    </div>}

                    {field === 'Local' && (
                        <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 p-4">
                            <span className="text-sm text-slate-300">Recebe Materiais?</span>
                            <button type="button" role="switch" aria-label="Recebe Materiais?" aria-checked={!!formData.receives}
                                onClick={() => setFormData({ ...formData, receives: !formData.receives })}
                                className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border transition-colors ${formData.receives ? 'border-emerald-500 bg-emerald-600' : 'border-slate-500 bg-slate-600'}`}>
                                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${formData.receives ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                        </div>
                    )}

                    {field === 'Tipo de Item' && <p className="rounded-xl border border-slate-400/25 p-3 text-sm">{category === 'SUPPLY' ? 'Este cadastro é um material de consumo.' : 'Este cadastro é uma peça ou componente.'}</p>}
                    {field === 'Tipo de Item' && formData.category !== 'SUPPLY' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 text-xs text-slate-300">
                                <input type="checkbox" checked={!!formData.affectsRanking}
                                       onChange={e => setFormData({...formData, affectsRanking: e.target.checked})} />
                                Participa do ranking
                            </label>
                            <label className="flex items-center gap-2 text-xs text-slate-300">
                                <input type="checkbox" checked={!!formData.isUnique}
                                       onChange={e => setFormData({...formData, isUnique: e.target.checked})} />
                                Componente único
                            </label>
                        </div>
                    )}

                    {field === 'Modelos de Itens' && category !== 'SUPPLY' && (
                        <div>
                            <label className="text-[10px] font-black uppercase text-slate-600 ml-1">Posição no ranking</label>
                            <input type="number" min={0}
                                   className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                   value={formData.ranking ?? 0}
                                   onChange={e => setFormData({...formData, ranking: Number(e.target.value)})} />
                        </div>
                    )}

                    {field === 'Empresas Terceirizadas' && (
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] font-black uppercase text-slate-600 ml-1">Nome Fantasia</label>
                                <input
                                    className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                    value={formData.fantasyName || ''}
                                    onChange={e => setFormData({...formData, fantasyName: e.target.value})}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black uppercase text-slate-600 ml-1">CNPJ</label>
                                    <input
                                        className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                        value={formData.cnpj || ''}
                                        onChange={e => setFormData({...formData, cnpj: formatCnpj(e.target.value)})}
                                        maxLength={18}
                                        autoCapitalize="characters"
                                        spellCheck={false}
                                        placeholder="14.397.462/0001-09"
                                        aria-invalid={Boolean(formData.cnpj) && !isValidCnpjFormat(formData.cnpj)}
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase text-slate-600 ml-1">Telefone</label>
                                    <input
                                        className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                        value={formData.telephone || ''}
                                        onChange={e => setFormData({...formData, telephone: formatPhone(e.target.value)})}
                                        maxLength={14}
                                        inputMode="tel"
                                        placeholder="(99)99999-9999"
                                        aria-invalid={Boolean(formData.telephone) && !isValidPhoneFormat(formData.telephone)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase text-slate-600 ml-1">Responsável / Contato</label>
                                <input
                                    className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-cyan-500"
                                    value={formData.contact || ''}
                                    onChange={e => setFormData({...formData, contact: e.target.value})} />
                            </div>
                        </div>
                    )}

                    {formError && (
                        <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="feedback-error text-xs"
                        >
                            {formError}
                        </motion.div>
                    )}

                    <button
                        onClick={handleSubmit}
                        className={`settings-submit-action w-full ${isEditing
                            ? 'bg-orange-600 hover:bg-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.3)]'
                            : 'bg-cyan-600 hover:bg-cyan-500 shadow-[0_0_20px_rgba(6,182,212,0.3)]'
                        } text-white font-black py-4 rounded-2xl transition-all flex items-center justify-center gap-2 mt-4`}>
                        <Check size={18}/> {isEditing ? 'ATUALIZAR REGISTRO' : 'SALVAR REGISTRO'}
                    </button>
                </div>
            </div>
        </motion.div></ModalPortal>
    );
};

// --- PÁGINA PRINCIPAL ---
const SystemSettingsPage = ({ mode = 'settings' }: { mode?: 'settings' | 'organization' | 'companies' }) => {
    const tabs = useMemo(() => mode === 'organization' ? [
        { id: 'teams', label: 'Equipe de TI', icon: Building2, fields: [] },
        ...['Secretaria', 'Unidade', 'Local'].map(field => ({ id: field, label: field, icon: Building2, fields: [field] })),
    ] : mode === 'companies' ? [{ id: 'companies', label: 'Empresas Terceirizadas', icon: Building, fields: ['Empresas Terceirizadas'] }] : CONFIG_TABS, [mode]);
    const [activeTab, setActiveTab] = useState(tabs[0].id);
    useEffect(() => { setActiveTab(tabs[0].id); }, [tabs]);
    const catalogCategory = activeTab === 'materials' ? 'SUPPLY' : activeTab === 'components' ? 'COMPONENT' : undefined;
    const [modalField, setModalField] = useState<string | null>(null);
    const [refreshCounters, setRefreshCounters] = useState<Record<string, number>>({});
    const [editingItem, setEditingItem] = useState<any | null>(null);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const handleSave = async (payload: any) => {
        if (!modalField) return;
        // Criamos uma cópia limpa para não afetar o estado do formulário na UI
        const cleanPayload = { ...payload };

        cleanPayload.itTeamId ||= payload.itTeam?.id;
        cleanPayload.secretaryId ||= payload.secretary?.id;
        cleanPayload.unitId ||= payload.unit?.id;
        cleanPayload.brandId ||= payload.brand?.id;
        cleanPayload.equipmentTypeId ||= payload.equipmentType?.id;
        cleanPayload.itemTypeId ||= payload.itemType?.id;
        cleanPayload.familyId ||= payload.family?.id;
        cleanPayload.categoryId ||= payload.category?.id;

        // Lista de objetos aninhados que o Spring não deve receber como objeto, apenas o ID
        const nestedObjects = ['itTeam', 'unit', 'secretary', 'brand', 'equipmentType', 'itemType', 'osFamily', 'category', 'family'];

        // Removemos os objetos aninhados do payload
        nestedObjects.forEach(key => {
            if (key !== 'category' || typeof cleanPayload[key] !== 'string') delete cleanPayload[key];
        });

        if(modalField === 'Tipo de Item') { cleanPayload.category = catalogCategory || cleanPayload.category; if(cleanPayload.category === 'SUPPLY') cleanPayload.affectsRanking = false; }
        if(modalField === 'Modelos de Itens' && catalogCategory === 'SUPPLY') cleanPayload.ranking = 0;
        try {
            if (editingItem) {
                // No PUT, garantimos que o ID do recurso está na URL
                await api.put(`/${endpointMap[modalField]}/${editingItem.id}`, cleanPayload);
            } else {
                // No POST, enviamos o payload limpo
                await api.post(`/${endpointMap[modalField]}`, cleanPayload);
            }

            setRefreshCounters(prev => ({
                ...prev,
                [modalField]: (prev[modalField] || 0) + 1
            }));
            closeModal();
        } catch (err: any) {
            console.error("Erro na requisição:", err);

            // Extrai a mensagem de erro do backend se existir, ou usa a padrão
            const msg = err.response?.data?.message || "Verifique se os campos obrigatórios estão preenchidos ou se já existe um registro com este nome.";
            setErrorMessage(msg);
            setShowError(true);
        }
    };

    const closeModal = () => {
        setModalField(null);
        setEditingItem(null);
    };

    return (
        <div className="page-content overflow-x-hidden">
            <AnimatePresence>
                {modalField && (
                    <RegistrationModal
                        field={modalField}
                        category={catalogCategory}
                        initialData={editingItem}
                        onClose={closeModal}
                        onSave={handleSave}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {showError && (
                    <CoreDialog
                        isOpen={showError}
                        onClose={() => setShowError(false)}
                        type="danger"
                        title="Falha na Operação"
                        description={errorMessage}
                        confirmLabel="Entendido"
                        onConfirm={() => setShowError(false)}
                    />
                )}
            </AnimatePresence>
            <div className="w-full space-y-4">
                <div className="grid grid-cols-12 gap-3 md:gap-4">
                    <div className="col-span-12 lg:col-span-3 space-y-2">
                        {tabs.map((tab) => (
                            <button key={tab.id} aria-pressed={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} className={`settings-tab w-full flex items-center justify-between p-5 rounded-[1.5rem] transition-all border ${activeTab === tab.id ? 'bg-cyan-500/10 border-cyan-500/30 text-white' : 'bg-[#161e2d] border-transparent text-slate-500'}`}>
                                <div className="flex items-center gap-4">
                                    <tab.icon size={20} className={activeTab === tab.id ? 'text-cyan-500' : ''} />
                                    <span className="text-xs font-black uppercase tracking-widest">{tab.label}</span>
                                </div>
                                {activeTab === tab.id && <div className="settings-active-indicator w-2 h-2 bg-cyan-500 rounded-full" />}
                            </button>
                        ))}
                    </div>

                    <div className="col-span-12 lg:col-span-9">
                        <div className="ui-card min-h-[600px] p-6 md:p-10">
                            <AnimatePresence mode="wait">
                                <motion.div key={activeTab} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    {activeTab === 'teams' && <div className="md:col-span-2"><AdministrationPage module="teams" /></div>}
                                    {tabs.find(t => t.id === activeTab)?.fields.map((field) => (
                                        <div key={field} className="space-y-4">
                                            <div className="flex items-center justify-between border-b border-white/5 pb-3">
                                                <h3 className="text-[11px] font-black text-white uppercase tracking-[0.2em] italic">{field === 'Tipo de Item' && catalogCategory ? (catalogCategory === 'SUPPLY' ? 'Tipos de materiais' : 'Tipos de componentes') : field === 'Modelos de Itens' && catalogCategory ? (catalogCategory === 'SUPPLY' ? 'Modelos de materiais' : 'Modelos de componentes') : field}</h3>
                                                <button onClick={() => setModalField(field)} className="ui-button-primary">+ Novo</button>
                                            </div>
                                            <InfiniteList
                                                field={field}
                                                category={catalogCategory}
                                                refreshKey={refreshCounters[field] || 0}
                                                onEdit={(item) => {
                                                    setEditingItem(item);
                                                    setModalField(field);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default SystemSettingsPage;
